import { Alert, Box, Button, Chip, CircularProgress, FormControl, InputAdornment, InputLabel, MenuItem, Select, TextField, Typography, alpha, useMediaQuery, useTheme } from '@mui/material'
import React, { useEffect, useState } from 'react'
import {
  CloudSyncOutlined, DnsOutlined, LanOutlined, LanguageOutlined, SaveOutlined, SpeedOutlined, ToggleOnOutlined,
} from '@mui/icons-material';

import { pageSxFor } from '../components/pageLayout';
// import { useAddFirstServerMutation, useGetFirstServerIPsQuery, useGetServerInfosQuery } from '../state/api';
import { getVpnServerInfo } from '../state/curlApi';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuthContext } from "../context/auth";
import { usePathContext } from "../context/path";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import useDialogStyles from '../hooks/useDialogStyles';
import { useFirstServerIPsContext } from '../context/firstServerIPs';
import { SubmitButton, SwitchField } from '../components/dialogKit';
import { EditPageHeader, SectionCard, SegmentedField } from '../components/vpn/formParts';
import { FONT_MONO } from '../config/fonts';

const STATUS_OPTIONS = [
  { label: 'Inactive', value: 0 },
  { label: 'Active', value: 1 },
];

const TARGET_OPTIONS = [
  { label: 'ALL', value: 0 },
  { label: 'ET', value: 1 },
  { label: 'DU', value: 2 },
  { label: 'OT', value: 3 },
];

const EditServer = () => {
  // theme
  const theme = useTheme();
  const { fieldSx, menuProps } = useDialogStyles();
  const accent = theme.palette.secondary.main;
  const { auth } = useAuthContext();
  const { setTitle } = usePathContext()
  const { firstServersIPs, serverInfos, dispatch } = useFirstServerIPsContext()
  const axiosPrivate = useAxiosPrivate()

  const navigate = useNavigate();

  const { serverId } = useParams();
  const location = useLocation();
  const { server } = location.state || {};

  // is large desktop screen
  const isNonMediumScreen = useMediaQuery("(min-width: 1200px)");

  const [page] = useState(0);
  const [pageSize] = useState(20);
  const [sort] = useState({});
  const [search] = useState("");

  useEffect(() => {
    setTitle("Edit FirstServer")

    const abortController = new AbortController()
    const getFirstServerIPs = async () => {
      try {
        const response = await axiosPrivate.get('/api/vpnManagement/getfirstserverips', {
          params: { page, pageSize, sort: JSON.stringify(sort), search, serverId,},
          signal: abortController.signal
        })
        dispatch({type: 'SET_FIRSTSERVERIPS', payload: response.data})
      } catch (err) {
        // the form still works without the IP list
      }
    }

    if(auth){
        getFirstServerIPs()
    }

    return () => {
      abortController.abort()
    }
  },[auth, axiosPrivate, dispatch, page, pageSize, search, serverId, setTitle, sort]);

  const [selectedServerIP, setSelectedServerIP] = useState("");

  const [ip, setIP] = useState('');
  const [port, setPort] = useState(80);
  const [vpnServerInfo, setVpnServerInfo] = useState(null);
  const [isLoadingVpnServerInfo, setIsLoadingVpnServerInfo] = useState(false);
  const [vpnError, setVpnError] = useState(null);
  const [serverPort, setServerPort] = useState(443);
  const [domainName, setDomainName] = useState('');
  const [fakeDomainName, setFakeDomainName] = useState('');
  const [maxUserCount, setMaxUserCount] = useState(800);
  const [maxSpeed, setMaxSpeed] = useState(380);
  const [serverPrice, setServerPrice] = useState(45);
  const [serverIPList, setServerIPList] = useState([]);
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const abortController = new AbortController()
    const getServerInfos = async () => {
      try {
        const response = await axiosPrivate.get('/api/vpnManagement/getServerInfos');
        dispatch({type: 'SET_SERVERINFOS', payload: response.data})
      } catch (err) {
        setFormError('Could not load server locations and providers.')
      }
    }

    if(auth){
        getServerInfos()
    }

    return () => {
      abortController.abort()
    }
  },[auth, axiosPrivate, dispatch]);

  const handleGetVPNServerInfo = async () => {
    setIsLoadingVpnServerInfo(true);
    setVpnError(null);
    try {
      const info = await getVpnServerInfo(ip, port);
      setVpnServerInfo(info);

      setServerPort(info.port);
      setDomainName(info.domain);
      if (info.ovpnArr) {
        const ipList = info.ovpnArr.map((item, index) => ({
          label: item.ipaddr,
          value: index,
        }));
        setServerIPList(ipList);

        // Pre-select the first IP in the list initially, if needed
        if (ipList.length > 0 && !selectedServerIP) {
          setSelectedServerIP(ipList[0].label);
        } else if (selectedServerIP && !ipList.some(ip => ip.label === selectedServerIP)) {
          setSelectedServerIP(""); // Reset if selected IP is not in the list
        }
      }
    } catch (error) {
      setVpnError(error.message || 'Could not reach the server');
    } finally {
      setIsLoadingVpnServerInfo(false);
    }
  };

  const handleBackClick = () => {
    navigate("/vpnservers");
  };

  const getCountryAndCity = (serverLocation) => {
    const region = serverInfos.serverRegions?.find(
      (region) => serverLocation === `${region.country} (${region.city})`
    );

    if (region) {
      return { country: region.country, city: region.city };
    }

    return ""; // Return null or any other default value if not found
  };

  const isAddServer = serverId === '0';

  const serverIP = firstServersIPs ? firstServersIPs?.combinedData?.find(serverIP => serverIP.ip === server?.mainIP) : {};

  const [selectServerLocation, setSelectServerLocation] = useState("");
  const [useDomain, setUseDomain] = useState(serverIP?.use_domain ?? 1);
  const [useFakeDomain, setUseFakeDomain] = useState(serverIP?.use_fakedomain ?? 0);
  const [selectServerStatus, setSelectServerStatus] = useState(server?.status ?? 0);
  const [selectTarget, setSelectTarget] = useState(server?.server_target ?? 0);
  const [selectServerProvider, setSelectServerProvider] = useState(server?.server_provider ?? "");

  useEffect(() => {
    if (isAddServer === false) {
      if(server?.countryLong && server?.city) {
        setSelectServerLocation(`${server.countryLong} (${server.city})`);
      }
      if(server?.mainIP) {
        setIP(server.mainIP);
      }
      if(serverIP?.port) {
        setServerPort(serverIP.port);
      }
      if(serverIP?.domain_name) {
        setDomainName(serverIP?.domain_name);
      }
      if(serverIP?.use_domain) {
        setUseDomain(serverIP?.use_domain);
      }
      if(serverIP?.fake_domain) {
        setFakeDomainName(serverIP?.fake_domain);
      }
      if(serverIP?.use_fakedomain) {
        setUseFakeDomain(serverIP?.use_fakedomain);
      }
      if(server?.maxUserCount) {
        setMaxUserCount(server?.maxUserCount);
      }
      if(server?.max_speed) {
        setMaxSpeed(server?.max_speed);
      }
      if(server?.status) {
        setSelectServerStatus(server?.status);
      }
      if(server?.server_target) {
        setSelectTarget(server?.server_target);
      }
      if(server?.server_provider) {
        setSelectServerProvider(server?.server_provider);
      }
      if(server?.server_price) {
        setServerPrice(server?.server_price);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isAddServer && ip && !vpnServerInfo) {
      handleGetVPNServerInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ip]);

  const handleSaveClick = async () => {
    if(!selectServerLocation || selectServerLocation === "") {
      setFormError('Select a server location.');
      return;
    }
    if(!selectServerProvider || selectServerProvider === "") {
      setFormError('Select a server provider.');
      return;
    }
    setFormError(null);

    const countryCity = getCountryAndCity(selectServerLocation);
    const formattedOpenPorts = isAddServer === false ? (server.open_ports) : (vpnServerInfo ? vpnServerInfo.open_ports : "");

    const serverData = {
      server_id: serverId,
      mainIP: ip,
      open_ports: formattedOpenPorts,
      control_port: port,
      countryLong: countryCity.country,
      city: countryCity.city,
      ipCount: serverIPList.length,
      activeip_count: 1,
      maxUserCount: maxUserCount,
      onlineUserCount: 0,
      max_speed: maxSpeed,
      cur_speed: 0,
      bandwidth: 0,
      server_provider: selectServerProvider,
      server_price: serverPrice,
      server_type: vpnServerInfo?vpnServerInfo.server_type:0,
      server_target: selectTarget,
      status: selectServerStatus,
      ping_status: 0,
      port: port,
      domain_name: domainName,
      use_domain: useDomain,
      fake_domain: fakeDomainName,
      use_fakedomain: useFakeDomain,
      serverInfo: vpnServerInfo?vpnServerInfo:'',
    };

    setSaving(true);
    try {
      const response = await axiosPrivate.post('/api/vpnManagement/addfirstserver', {
        params: { serverData,},
      })
      dispatch({type: 'SET_FIRSTSERVER', payload: response.data})

      navigate("/vpnservers");
    } catch (error) {
      console.error('Failed to add server:', error);
      setFormError(error.response?.data?.error || 'Failed to save the server.');
    } finally {
      setSaving(false);
    }
  };

  // loader
  if (!serverInfos) {
    return (
      <Box sx={{ ...pageSxFor(isNonMediumScreen), alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
        <CircularProgress size={28} sx={{ color: accent }} />
        <Typography sx={{ color: theme.palette.text.secondary }}>Loading server infos…</Typography>
      </Box>
    );
  }

  const numberField = (label, value, setValue, unit) => (
    <TextField
      size="small"
      label={label}
      type="number"
      value={value}
      onChange={(e) => setValue(Number(e.target.value))}
      fullWidth
      sx={fieldSx}
      InputProps={unit ? { endAdornment: <InputAdornment position="end">{unit}</InputAdornment> } : undefined}
    />
  );

  // what the server reported after "Get"
  const infoRow = (label, value) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.75, borderBottom: `1px dashed ${theme.palette.divider}` }}>
      <Typography sx={{ fontSize: '0.8rem', color: theme.palette.text.secondary }}>{label}</Typography>
      <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, fontFamily: FONT_MONO, textAlign: 'right', wordBreak: 'break-all' }}>
        {value === undefined || value === null || value === '' ? '—' : String(value)}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ ...pageSxFor(isNonMediumScreen), height: 'auto' }}>
      <EditPageHeader
        icon={DnsOutlined}
        title={isAddServer ? 'Add Server' : 'Edit Server'}
        subtitle={isAddServer ? 'Connect to a new VPN server and register it' : `Server ID ${serverId}${ip ? ` · ${ip}` : ''}`}
        onBack={handleBackClick}
      >
        <SubmitButton
          label={isAddServer ? 'Add server' : 'Save changes'}
          busyLabel="Saving…"
          busy={saving}
          disabled={!vpnServerInfo}
          onClick={handleSaveClick}
          icon={<SaveOutlined />}
        />
      </EditPageHeader>

      {formError && (
        <Alert severity="error" onClose={() => setFormError(null)} sx={{ mb: 2 }}>
          {formError}
        </Alert>
      )}
      {!vpnServerInfo && !isLoadingVpnServerInfo && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Enter the main IP and control port, then press <b>Get</b>: the server info is needed before saving.
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1.6fr 1fr' },
          gap: 2.5,
          alignItems: 'start',
        }}
      >
        {/* ---------- left: the form ---------- */}
        <Box sx={{ display: 'grid', gap: 2.5, minWidth: 0 }}>
          <SectionCard icon={LanOutlined} title="Connection" subtitle="Where the server is and how to reach it">
            <FormControl fullWidth size="small" sx={fieldSx}>
              <InputLabel>Server location</InputLabel>
              <Select
                label="Server location"
                value={selectServerLocation}
                onChange={(e) => setSelectServerLocation(e.target.value)}
                MenuProps={menuProps}
              >
                {serverInfos.serverRegions?.map((region) => (
                  <MenuItem key={`${region.country}_${region.city}`} value={`${region.country} (${region.city})`}>
                    {`${region.country} (${region.city})`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 120px auto' }, gap: 1.5, alignItems: 'center' }}>
              <TextField
                size="small"
                label="Main server IP"
                placeholder="203.0.113.10"
                value={ip}
                onChange={(e) => setIP(e.target.value)}
                fullWidth
                autoComplete="off"
                sx={fieldSx}
                InputProps={{ sx: { fontFamily: FONT_MONO } }}
              />
              <TextField
                size="small"
                label="Control port"
                type="number"
                value={port}
                onChange={(e) => setPort(Number(e.target.value))}
                sx={fieldSx}
                InputProps={{ sx: { fontFamily: FONT_MONO } }}
              />
              <Button
                variant="outlined"
                onClick={handleGetVPNServerInfo}
                disabled={isLoadingVpnServerInfo || ip === ''}
                startIcon={isLoadingVpnServerInfo ? <CircularProgress size={16} color="inherit" /> : <CloudSyncOutlined />}
                sx={{
                  height: 40,
                  textTransform: 'none',
                  fontWeight: 700,
                  color: accent,
                  borderColor: alpha(accent, 0.5),
                  '&:hover': { borderColor: accent, backgroundColor: alpha(accent, 0.08) },
                }}
              >
                {isLoadingVpnServerInfo ? 'Getting…' : 'Get info'}
              </Button>
            </Box>

            {vpnError && <Alert severity="error">Could not get the server info: {vpnError}</Alert>}

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: isAddServer ? '1fr 1fr' : '1fr' }, gap: 1.5 }}>
              {isAddServer && (
                <FormControl fullWidth size="small" sx={fieldSx} disabled={serverIPList.length === 0}>
                  <InputLabel>Server IP</InputLabel>
                  <Select
                    label="Server IP"
                    value={selectedServerIP}
                    onChange={(e) => setSelectedServerIP(e.target.value)}
                    MenuProps={menuProps}
                  >
                    {serverIPList.map((option) => (
                      <MenuItem key={option.value} value={option.label} sx={{ fontFamily: FONT_MONO }}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <TextField
                size="small"
                label="VPN port"
                value={serverPort ?? ''}
                fullWidth
                sx={fieldSx}
                helperText="Reported by the server"
                InputProps={{ readOnly: true, sx: { fontFamily: FONT_MONO } }}
              />
            </Box>
          </SectionCard>

          <SectionCard icon={LanguageOutlined} title="Domains" subtitle="Names clients connect with">
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 220px' }, gap: 1.5, alignItems: 'start' }}>
              <TextField size="small" label="Domain" value={domainName || ''} onChange={(e) => setDomainName(e.target.value)} fullWidth sx={fieldSx} />
              <SwitchField
                label="Use domain"
                checked={Number(useDomain) === 1}
                onChange={(on) => setUseDomain(on ? 1 : 0)}
                onText="Clients use this name"
                offText="Not used"
              />
              <TextField size="small" label="HTTPS domain" value={fakeDomainName || ''} onChange={(e) => setFakeDomainName(e.target.value)} fullWidth sx={fieldSx} />
              <SwitchField
                label="Use HTTPS domain"
                checked={Number(useFakeDomain) === 1}
                onChange={(on) => setUseFakeDomain(on ? 1 : 0)}
                onText="Clients use this name"
                offText="Not used"
              />
            </Box>
          </SectionCard>

          <SectionCard icon={SpeedOutlined} title="Capacity & provider">
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
              {numberField('Max online users', maxUserCount, setMaxUserCount, 'users')}
              {numberField('Max speed', maxSpeed, setMaxSpeed, 'Mbps')}
              <FormControl fullWidth size="small" sx={fieldSx}>
                <InputLabel>Server provider</InputLabel>
                <Select
                  label="Server provider"
                  value={selectServerProvider}
                  onChange={(e) => setSelectServerProvider(e.target.value)}
                  MenuProps={menuProps}
                >
                  {serverInfos.serverProviders?.map((option) => (
                    <MenuItem key={option.name} value={option.name}>{option.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {numberField('Server price', serverPrice, setServerPrice)}
            </Box>
          </SectionCard>

          <SectionCard icon={ToggleOnOutlined} title="Status & target">
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1.4fr' }, gap: 1.5 }}>
              <SegmentedField label="Server status" value={Number(selectServerStatus)} options={STATUS_OPTIONS} onChange={setSelectServerStatus} />
              <SegmentedField label="Target" value={Number(selectTarget)} options={TARGET_OPTIONS} onChange={setSelectTarget} />
            </Box>
          </SectionCard>
        </Box>

        {/* ---------- right: what the server reported ---------- */}
        <SectionCard
          icon={DnsOutlined}
          title="Server info"
          subtitle="Read from the server with Get info"
          sx={{ position: { lg: 'sticky' }, top: { lg: 0 } }}
          action={
            isLoadingVpnServerInfo ? (
              <CircularProgress size={18} sx={{ color: accent }} />
            ) : (
              <Chip
                size="small"
                label={vpnServerInfo ? 'Connected' : vpnError ? 'Failed' : 'Not loaded'}
                sx={{
                  fontWeight: 700,
                  color: vpnServerInfo ? theme.palette.success.main : vpnError ? theme.palette.error.main : theme.palette.text.secondary,
                  backgroundColor: alpha(vpnServerInfo ? theme.palette.success.main : vpnError ? theme.palette.error.main : theme.palette.text.secondary, 0.14),
                }}
              />
            )
          }
        >
          {vpnServerInfo ? (
            <Box>
              {infoRow('Server type', vpnServerInfo.server_type)}
              {infoRow('VPN port', vpnServerInfo.port)}
              {infoRow('Domain', vpnServerInfo.domain)}
              {infoRow('Open ports', vpnServerInfo.open_ports)}
              {infoRow('IP addresses', serverIPList.length)}
              {serverIPList.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.5 }}>
                  {serverIPList.map((o) => (
                    <Chip
                      key={o.value}
                      size="small"
                      label={o.label}
                      sx={{
                        fontFamily: FONT_MONO,
                        fontSize: '0.75rem',
                        color: o.label === selectedServerIP ? accent : theme.palette.text.primary,
                        backgroundColor: alpha(accent, o.label === selectedServerIP ? 0.16 : 0.06),
                        border: `1px solid ${alpha(accent, o.label === selectedServerIP ? 0.5 : 0.15)}`,
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ py: 4, textAlign: 'center', color: theme.palette.text.secondary }}>
              <CloudSyncOutlined sx={{ fontSize: 40, color: alpha(accent, 0.6), mb: 1 }} />
              <Typography sx={{ fontSize: '0.85rem' }}>
                {isLoadingVpnServerInfo ? 'Contacting the server…' : 'No server info yet'}
              </Typography>
            </Box>
          )}
        </SectionCard>
      </Box>
    </Box>
  )
}

export default EditServer
